import { Dealer } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { CustomGoogleMap, GOOGLE_MAP_PROPS, Text, useFindDealerContext } from '@components';
import { useAppSelector } from '@hooks';
import { Backdrop, Box, CircularProgress, ClickAwayListener, Link, Stack } from '@mui/material';
import { InfoWindow, Marker, MarkerClusterer } from '@react-google-maps/api';
import { getSpecialPage } from '@store/app/app.slice';
import { colors } from '@themes/colors';
import { t } from '@translations';
import { useCallback, useState } from 'react';
import { getPublicAddress } from './FindDealer.config';

type FindDealerMapProps = {
  isWorking: boolean;
};

export const FindDealerMap = ({ isWorking }: FindDealerMapProps) => {
  const { updateInstanceMap, map, currentDealerList, searchValue, userLocation } =
    useFindDealerContext();
  const [showMarker, setShowMarker] = useState<Dealer | null>(null);

  const onLoad = useCallback(
    function callback(mapInstance: google.maps.Map) {
      updateInstanceMap(mapInstance);
    },
    [updateInstanceMap]
  );

  const onUnmount = useCallback(
    function callback() {
      updateInstanceMap(null);
    },
    [updateInstanceMap]
  );

  const greenDotIcon = {
    path: google.maps.SymbolPath.CIRCLE,
    fillColor: colors.primary.darkGreen,
    fillOpacity: 1,
    scale: 7,
    strokeColor: colors.primary.darkGreen,
    strokeWeight: 9,
    strokeOpacity: 0.5,
  };

  return (
    <ClickAwayListener onClickAway={() => setShowMarker(null)}>
      <Box position='relative' sx={{ height: { lg: '891px', xs: '591px' }, width: '100%' }}>
        <CustomGoogleMap
          zoom={map?.getZoom() || GOOGLE_MAP_PROPS.minzoom}
          center={map?.getCenter() || GOOGLE_MAP_PROPS.center}
          mapContainerStyle={{ height: '100%', width: '100%' }}
          onLoad={onLoad}
          onUnmount={onUnmount}
        >
          <MarkerClusterer>
            {(clusterer) => (
              <>
                {currentDealerList?.map((location, i) => (
                  <Marker
                    key={location.id}
                    position={{ lat: location?.latitude || 0, lng: location?.longitude || 0 }}
                    clusterer={clusterer}
                    onClick={() => setShowMarker(location)}
                  >
                    {showMarker === location && (
                      <InfoWindow onCloseClick={() => setShowMarker(null)}>
                        <InfoWindowContent dealer={location} />
                      </InfoWindow>
                    )}
                  </Marker>
                ))}
                {searchValue?.type === 'place' && (
                  <Marker
                    position={{ lat: searchValue.lat, lng: searchValue.lng }}
                    icon={greenDotIcon}
                  />
                )}
                {userLocation && (
                  <Marker
                    position={{ lat: userLocation.lat, lng: userLocation.lng }}
                    icon={greenDotIcon}
                  />
                )}
              </>
            )}
          </MarkerClusterer>
        </CustomGoogleMap>
        <Backdrop open={isWorking} sx={{ color: '#fff', position: 'absolute', zIndex: 100 }}>
          <CircularProgress color='inherit' />
        </Backdrop>
      </Box>
    </ClickAwayListener>
  );
};

const InfoWindowContent = ({ dealer }: { dealer: Dealer }) => {
  const { updateMarkerDetail, map } = useFindDealerContext();

  const dealerDetailsBaseUrl = useAppSelector(getSpecialPage('dealerDetailsPage'));
  const dealerDetailsHref = (dealer: Dealer) =>
    dealer?.customerNo && `${dealerDetailsBaseUrl}?id=${dealer?.customerNo}`;

  const onMarkerClick = (marker: Dealer) => {
    map?.panTo({
      lat: marker.latitude || 0,
      lng: marker.longitude || 0,
    });
    map?.setZoom(GOOGLE_MAP_PROPS.maxzoom);
    updateMarkerDetail(marker);
  };
  return (
    <Box sx={{ my: 1, maxWidth: 200 }}>
      <InfoWindowText text={getPublicAddress(dealer)?.name} hasMb />
      <InfoWindowText
        text={getPublicAddress(dealer)?.line1}
        label={t(TextCodes.Dealer.Find.Address)}
      />
      <InfoWindowText
        text={getPublicAddress(dealer)?.city}
        label={t(TextCodes.Dealer.Find.City)}
        hasMb
      />
      <InfoWindowText
        text={getPublicAddress(dealer)?.contactEmail}
        label={t(TextCodes.Dealer.Find.ContactEmail)}
      />
      <InfoWindowText
        text={getPublicAddress(dealer)?.generalPhoneNumber}
        label={t(TextCodes.Dealer.Find.Phone)}
        hasMb
      />
      <Stack direction='row' spacing={1}>
        <Link onClick={() => onMarkerClick(dealer)} sx={{ cursor: 'pointer' }}>
          Zoom
        </Link>
        {dealerDetailsHref(dealer) && (
          <Link href={dealerDetailsHref(dealer)}>{t(TextCodes.Dealer.Find.DealerDetails)}</Link>
        )}
      </Stack>
    </Box>
  );
};

const InfoWindowText = ({
  text,
  label,
  hasMb,
}: {
  text?: string | null;
  label?: string;
  hasMb?: boolean;
}) => {
  return text ? (
    <Text sx={{ fontSize: '12px !important', ...(hasMb && { mb: 1 }) }}>
      {label && `${label}:`} {text}
    </Text>
  ) : null;
};
